import { useCallback } from 'react'

import Flex from '@/elements/Flex'
import FormToggle from '@/elements/FormToggle'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useServices } from '@/hooks/useServices'
import type { ServiceSettingsResponse } from '@/api/types/Service'
import { updateOrganizationServicesSettings, useGetOrganizationServicesSettings } from '@/api/service'
import toast from '@/utils/toast'

function OrganizationServices() {
  const { isTablet, isMobile } = useBreakpoints()
  const {
    serviceGrouping,
    getServiceTypeName,
    getServiceTypeDescription
  } = useServices()
  const { data, isLoading, refetch } = useGetOrganizationServicesSettings()
  const serviceSettings = data?.data || []

  const submitUpdateServices = useCallback(async (
    id: ServiceSettingsResponse['id'],
    value: ServiceSettingsResponse['value']
  ) => {
    try {
      await updateOrganizationServicesSettings(id, value)
      toast.success('Service updated')
    } catch {
      toast.error('Failed to update service')
    }
    await refetch()
  }, [refetch])

  return (
    <Flex
      className='card'
      wrap
      grow
      wfull
      inline={!isTablet}
      gap={7}
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      <Flex fraction={4} gap={2} wfull>
        <Text variant='h3'>Services</Text>
        <Text color='black-light-40'>
          Manage organization services and integrations.
        </Text>
      </Flex>
      <Flex fraction={6} wfull>
        <Flex gap={5} wfull>
          {!isLoading ? (Object.entries(serviceGrouping).map(([groupName, types]) => {
            return (
              <Flex gap={3} key={groupName} wfull>
                <Text variant='callout' color='black-light-40'>
                  {groupName}
                </Text>
                {types.map(type => {
                  const setting = serviceSettings.find(s => s.service.type === type)
                  if (!setting) return null
                  return (
                    <Flex
                      key={setting.id}
                      wfull
                      inline
                      gap={2}
                      align='center'
                      justify='space-between'
                    >
                      <Flex wfull mr={2}>
                        <Text variant='h4' color='black'>
                          {getServiceTypeName(setting.service.type)}
                        </Text>
                        <Text variant='textblock' color='black-light-40'>
                          {getServiceTypeDescription(setting.service.type)}
                        </Text>
                      </Flex>
                      <FormToggle
                        name={setting.service.type}
                        value={setting.value}
                        onChange={event => submitUpdateServices(setting.id, event.target.checked)}
                      />
                    </Flex>
                  )
                })}
              </Flex>
            )
          })) : (
            <Flex wfull style={{ height: 200 }}><Loading /></Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
export default OrganizationServices
